/**
 * Administration → Webhooks: the endpoints workflows may call, with the
 * signing secret shown once, extra headers kept encrypted, a test call and
 * the outcome of the last call. Also explains inbound webhook triggers.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Loader2, Pencil, Plus, Send, Trash2, Webhook } from 'lucide-react';
import type { WebhookEndpointDTO } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useWebhookEndpoints } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { StatusDot } from '../../components/Status';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { relative } from '../../lib/format';
import { copy } from '../../lib/clipboard';
import { queryKeys } from '../../api/queryKeys';
// 1.6.0 (docs/dev/mcp-1.6.0-build.md): site services (W) and the AI endpoint templates (A).
import { SiteServicesSection } from './webhooks/SiteServices';
import { AiEndpointTemplates } from './webhooks/AiEndpointTemplates';

/** 1.6.0: a new endpoint's first values (AI endpoint templates, A); the key's value is typed by the administrator. */
export interface EndpointPreset {
  name: string; url: string; method: WebhookEndpointDTO['method']; description: string;
  headers: Array<{ name: string; value: string }>; mayReceiveMediaLinks: boolean;
}

export function WebhooksAdmin() {
  const q = useWebhookEndpoints();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WebhookEndpointDTO | 'new' | null>(null);
  const [preset, setPreset] = useState<EndpointPreset | null>(null);
  const [secret, setSecret] = useState<{ name: string; secret: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.webhookEndpoints() });

  async function act<T>(e: WebhookEndpointDTO, fn: () => Promise<T>, done: (r: T) => void) {
    setBusy(e.id);
    try {
      done(await fn());
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page" data-testid="webhooks-admin">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h2>Webhooks</h2>
          <p className="lede">
            Systems that workflows may call with a Call webhook or Repeat until step: POST or PUT a JSON body, or GET with query parameters
            (for example to look up programme data or poll a job). Every call is signed with the endpoint’s secret in <span className="mono">X-Prism-Signature</span> and
            carries an <span className="mono">Idempotency-Key</span>. Extra headers, such as the receiving system’s API key, are stored encrypted and never shown again.
          </p>
        </div>
        {/* 1.6.0 (A, webhooks/AiEndpointTemplates.tsx): Claude, OpenAI, Gemini, OpenRouter. */}
        <AiEndpointTemplates onPick={(p) => { setPreset(p); setEditing('new'); }} />
        <button className="btn primary" onClick={() => { setPreset(null); setEditing('new'); }} data-testid="new-endpoint"><Plus size={15} /> New endpoint</button>
      </div>
      {items.length === 0 && !q.isLoading ? (
        <EmptyState style={{ border: '1px dashed var(--border-strong)', borderRadius: 14 }} icon={<Webhook size={24} />} title="No endpoints yet">
          <p>Add one for each system workflows should notify or ask.</p>
        </EmptyState>
      ) : (
        <table className="table" aria-label="Webhook endpoints">
          <thead><tr><th>Name</th><th>Call</th><th>Used by</th><th>Last call</th><th aria-label="Actions" /></tr></thead>
          <tbody>{items.map((e) => (
            <tr key={e.id} data-testid="endpoint-row" data-name={e.name} style={{ cursor: 'default', opacity: e.enabled ? 1 : 0.6 }}>
              <td style={{ whiteSpace: 'normal' }}><strong>{e.name}</strong>{!e.enabled && <span className="pill" style={{ marginLeft: 6 }}>Disabled</span>}
                {e.description && <div className="faint" style={{ fontSize: 12 }}>{e.description}</div>}</td>
              <td className="wrap" style={{ minWidth: 200 }}><span className="pill">{e.method}</span> <span className="mono" style={{ fontSize: 12 }}>{e.url}</span>
                {e.headerNames.length > 0 && <div className="faint" style={{ fontSize: 12 }}>with {e.headerNames.join(', ')}</div>}
                {/* 1.6.0: flows may send it signed media links. */}
                {e.mayReceiveMediaLinks && <div className="faint" style={{ fontSize: 12 }} data-testid="endpoint-media-links">May receive media links</div>}</td>
              <td className="muted" style={{ whiteSpace: 'normal' }}>{e.usedBy.length ? e.usedBy.join(', ') : 'No workflows'}</td>
              <td className="muted" style={{ whiteSpace: 'normal' }}>{e.lastCalledAt
                ? <><StatusDot tone={e.lastError ? 'failed' : 'ready'}>{e.lastStatus ?? 'no answer'}</StatusDot> {relative(e.lastCalledAt)}{e.lastError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{e.lastError}</div>}</>
                : 'Never'}</td>
              <td style={{ textAlign: 'right' }}>
                <button className="btn ghost sm" disabled={busy === e.id} onClick={() => void act(e, () => api<{ status: number; ok: boolean; error: string | null; response: string }>(`/api/v1/webhook-endpoints/${e.id}/test`, { method: 'POST', json: {} }),
                  (r) => toast(r.ok ? `${e.name} answered ${r.status}` : r.error ?? 'Failed', r.ok ? undefined : 'error'))} data-testid="test-endpoint">
                  {busy === e.id ? <Loader2 size={13} className="spin" /> : <Send size={13} />} Test</button>
                <button className="btn ghost sm" onClick={() => setEditing(e)} aria-label="Edit endpoint"><Pencil size={13} /></button>
                <button className="btn ghost sm" title="New signing secret" onClick={() => { if (window.confirm(`Make a new signing secret for “${e.name}”? The receiver must be given it before signatures verify again.`)) void act(e, () => api<{ secret: string }>(`/api/v1/webhook-endpoints/${e.id}/secret`, { method: 'POST', json: {} }), (r) => setSecret({ name: e.name, secret: r.secret })); }}><KeyRound size={13} /></button>
                <button className="btn ghost sm danger" aria-label="Remove endpoint" onClick={() => { if (window.confirm(`Remove “${e.name}”?`)) void act(e, () => api(`/api/v1/webhook-endpoints/${e.id}`, { method: 'DELETE' }), () => toast(`Removed ${e.name}`)); }}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}

      {/* 1.6.0 (W, webhooks/SiteServices.tsx): site services through storage agents, from the agents' reports. */}
      <SiteServicesSection />

      <h3 style={{ marginTop: 32 }}>Starting workflows from other systems</h3>
      <p className="lede" style={{ marginTop: 4 }}>
        A workflow with the <b>Authenticated webhook</b> trigger gets its own address and token (Webhook token on the workflow’s toolbar). Callers send
        <span className="mono"> Authorization: Bearer &lt;token&gt;</span> and either POST JSON or GET with query parameters, e.g.
        <span className="mono"> GET /hooks/v1/automations/&lt;id&gt;?assetId=…&amp;reference=PO-1</span>. Whatever they send is available to the workflow as
        <span className="mono"> {'{{trigger.payload.<key>}}'}</span>.
      </p>

      {editing && <EndpointDialog endpoint={editing === 'new' ? null : editing} preset={editing === 'new' ? preset : null} onClose={() => setEditing(null)}
        onSaved={(r) => { setEditing(null); void refresh(); if (r.secret) setSecret({ name: r.name, secret: r.secret }); }} />}
      {secret && <SecretDialog {...secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

function EndpointDialog({ endpoint, preset, onClose, onSaved }: { endpoint: WebhookEndpointDTO | null; preset?: EndpointPreset | null; onClose: () => void; onSaved: (r: { name: string; secret?: string }) => void }) {
  const [f, setF] = useState({
    name: endpoint?.name ?? preset?.name ?? '', url: endpoint?.url ?? preset?.url ?? 'https://', method: endpoint?.method ?? preset?.method ?? 'POST',
    description: endpoint?.description ?? preset?.description ?? '', enabled: endpoint?.enabled ?? true,
    // 1.6.0: flows may send it signed media links (off by default).
    mayReceiveMediaLinks: endpoint?.mayReceiveMediaLinks ?? preset?.mayReceiveMediaLinks ?? false,
  });
  // Header values are never shown again; editing them replaces all of them.
  const [headers, setHeaders] = useState<Array<{ name: string; value: string }>>(preset?.headers ?? []);
  const [replaceHeaders, setReplaceHeaders] = useState(!endpoint);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const h = Object.fromEntries(headers.filter((x) => x.name.trim()).map((x) => [x.name.trim(), x.value]));
      const body = { ...f, ...(replaceHeaders ? { headers: h } : {}) };
      const r = await api<WebhookEndpointDTO & { secret?: string }>(endpoint ? `/api/v1/webhook-endpoints/${endpoint.id}` : '/api/v1/webhook-endpoints', { method: endpoint ? 'PATCH' : 'POST', json: body });
      toast(endpoint ? `Saved ${f.name}` : `Added ${f.name}`);
      onSaved({ name: r.name, secret: r.secret });
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={endpoint ? `Edit ${endpoint.name}` : 'New webhook endpoint'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!f.name.trim() || !/^https?:\/\/./.test(f.url) || busy} onClick={() => void save()} data-testid="save-endpoint">Save</button>
    </>}>
      <div className="field-row">
        <div className="field"><label htmlFor="ep-name">Name</label><input id="ep-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Playout system" /></div>
        <div className="field"><label htmlFor="ep-method">Method</label>
          <select id="ep-method" className="select" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value as WebhookEndpointDTO['method'] })}>
            <option value="POST">POST (JSON body)</option><option value="PUT">PUT (JSON body)</option><option value="GET">GET (query parameters)</option>
          </select></div>
      </div>
      <div className="field"><label htmlFor="ep-url">URL</label><input id="ep-url" className="input mono" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
        <span className="help">HTTPS. Steps can add a path to it, such as <span className="mono">items/{'{{asset.metadata.f_house_id}}'}</span>. Private or plain-HTTP addresses need PRISM_WEBHOOK_ALLOW_HOSTS.</span></div>
      <div className="field"><label htmlFor="ep-desc">What it is for</label><input id="ep-desc" className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="field"><label>Extra headers</label>
        {endpoint && !replaceHeaders ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="muted">{endpoint.headerNames.length ? `Sends ${endpoint.headerNames.join(', ')} (values hidden)` : 'None'}</span>
            <button type="button" className="btn ghost sm" onClick={() => setReplaceHeaders(true)}>Replace headers</button>
          </div>
        ) : (
          <>
            {headers.map((h, i) => (
              <div className="condition-row" key={i}>
                <input className="input mono" aria-label="Header name" placeholder="Authorization" value={h.name} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input className="input mono" aria-label="Header value" type="password" placeholder="Bearer …" value={h.value} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                <button type="button" className="btn ghost icon sm" aria-label="Remove header" onClick={() => setHeaders(headers.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
              </div>
            ))}
            <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setHeaders([...headers, { name: '', value: '' }])}><Plus size={13} /> Header</button>
            <span className="help">For example the receiving system’s API key. Stored encrypted; {endpoint ? 'saving replaces all existing headers.' : 'only their names are shown later.'}</span>
          </>
        )}
      </div>
      <label className="toggle-row"><input type="checkbox" checked={f.mayReceiveMediaLinks} onChange={(e) => setF({ ...f, mayReceiveMediaLinks: e.target.checked })} data-testid="endpoint-media-links-toggle" />
        <span>May receive media links: flows may send this endpoint signed, time-limited links to the asset’s frames, proxy, exports and subtitles (for example an AI service describing shots). Off: such steps are refused.</span></label>
      {endpoint && <label className="toggle-row"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /><span>Enabled (disabled endpoints make their steps fail)</span></label>}
    </Modal>
  );
}

function SecretDialog({ name, secret, onClose }: { name: string; secret: string; onClose: () => void }) {
  const verify = `signature = HMAC_SHA256(secret, "<t>.<body>")   # GET: "<t>.<path>?<query>"\ncompare with v1 in X-Prism-Signature: t=<t>,v1=<hex>`;
  return (
    <Modal title={`Signing secret for ${name}`} wide onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
      <div className="banner warning"><div className="banner-body"><strong>Copy the secret now</strong><span>It is shown only once. Give it to the receiving system so it can verify that calls come from PRISM.</span></div></div>
      <pre className="code-block" data-testid="endpoint-secret">{secret}</pre>
      <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => copy(secret)}><Copy size={13} /> Copy secret</button>
      <pre className="code-block">{verify}</pre>
    </Modal>
  );
}
